import { gsap } from 'gsap';

function parseNumber(value) {
    if (value == null) return null;
    const parsed = Number.parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : null;
}

function parseDelay(value) {
    if (value == null) return 0;
    const raw = String(value).trim().toLowerCase();

    // Allow explicit units
    if (raw.endsWith('ms')) {
        const ms = parseNumber(raw.slice(0, -2));
        return ms ? ms / 1000 : 0;
    }

    if (raw.endsWith('s')) {
        const seconds = parseNumber(raw.slice(0, -1));
        return seconds ?? 0;
    }

    // Bare numbers: treat large values as ms (common authoring style: 100, 200, 300)
    const n = parseNumber(raw);
    if (!n) return 0;
    return n >= 20 ? n / 1000 : n;
}

function getPreset(name) {
    const preset = (name ?? '').toString().trim().toLowerCase();

    switch (preset) {
        case 'fade':
            return { from: { opacity: 0 }, to: { opacity: 1 } };
        case 'fade-down':
            return { from: { opacity: 0, y: -16 }, to: { opacity: 1, y: 0 } };
        case 'fade-left':
            return { from: { opacity: 0, x: -16 }, to: { opacity: 1, x: 0 } };
        case 'fade-right':
            return { from: { opacity: 0, x: 16 }, to: { opacity: 1, x: 0 } };
        case 'scale':
        case 'scale-in':
            return {
                from: { opacity: 0, scale: 0.96 },
                to: { opacity: 1, scale: 1 }
            };
        case 'fade-up':
        default:
            return { from: { opacity: 0, y: 16 }, to: { opacity: 1, y: 0 } };
    }
}

const animateIn = {
    initialized: false,
    observer: null,
    processedElements: new WeakSet(),

    init(root = document) {
        if (this.initialized) return;
        this.initialized = true;

        if (typeof window === 'undefined') return;

        const motionPreference = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        );
        motionPreference?.addEventListener?.('change', (event) => {
            const elements = document.querySelectorAll('[data-animate-in]');
            if (event.matches) {
                document.documentElement.classList.add('animate-in-disabled');
                this.observer?.disconnect();
                gsap.killTweensOf(elements);
            }

            // Keep already-rendered content visible when the preference changes.
            // Only content added after motion is re-enabled should animate in.
            gsap.set(elements, {
                opacity: 1,
                clearProps: 'transform,willChange'
            });

            if (!event.matches) {
                elements.forEach((el) => this.processedElements.add(el));
                document
                    .querySelectorAll('[data-animate-group]')
                    .forEach((group) => this.processedElements.add(group));
                document.documentElement.classList.remove(
                    'animate-in-disabled'
                );
                this.processElements(document);
            }
        });

        // Create a shared observer instance
        this.observer = new IntersectionObserver(
            (entries) => {
                if (
                    document.documentElement.classList.contains(
                        'animate-in-disabled'
                    )
                )
                    return;

                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;

                    const el = entry.target;
                    this.observer?.unobserve(el);

                    // Group: play all animated children inside it
                    if (el.hasAttribute('data-animate-group')) {
                        const children =
                            el.querySelectorAll('[data-animate-in]');
                        for (const child of children) {
                            this.play(child);
                        }
                    } else {
                        this.play(el);
                    }
                }
            },
            { threshold: 0.01 }
        );

        if (motionPreference?.matches) {
            document.documentElement.classList.add('animate-in-disabled');
            return;
        }

        this.processElements(root);
    },

    /**
     * Process elements within a root container.
     * Used for initial load and for refreshing after dynamic content loads.
     */
    processElements(root = document) {
        if (typeof window === 'undefined') return;
        if (!this.observer) return;

        const reduceMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        )?.matches;
        if (reduceMotion) return;

        // --- Groups: observe the parent, set initial state on children ---
        const groups = Array.from(
            root.querySelectorAll('[data-animate-group]')
        );
        for (const group of groups) {
            if (this.processedElements.has(group)) continue;
            this.processedElements.add(group);

            const children = Array.from(
                group.querySelectorAll('[data-animate-in]')
            );
            for (const child of children) {
                this.processedElements.add(child);
                const { from } = getPreset(child.dataset.animateIn);
                gsap.set(child, { ...from, willChange: 'transform,opacity' });
            }

            this.observer.observe(group);
        }

        // --- Standalone (ungrouped) elements ---
        const elements = Array.from(root.querySelectorAll('[data-animate-in]'));

        for (const el of elements) {
            if (this.processedElements.has(el)) continue;
            this.processedElements.add(el);

            const { from } = getPreset(el.dataset.animateIn);
            gsap.set(el, { ...from, willChange: 'transform,opacity' });
            this.observer.observe(el);
        }
    },

    /**
     * Refresh animations for newly added content (e.g., after htmx/Sprig swap).
     * Call this after dynamic content is inserted into the DOM.
     */
    refresh(root = document) {
        if (!this.initialized) {
            this.init(root);
            return;
        }
        this.processElements(root);
    },

    play(el) {
        if (!(el instanceof HTMLElement)) return;

        const { to } = getPreset(el.dataset.animateIn);
        const delay = parseDelay(el.dataset.animateDelay);
        const duration = parseNumber(el.dataset.animateDuration) ?? 0.8;

        gsap.to(el, {
            ...to,
            delay,
            duration,
            ease: 'power2.out',
            overwrite: true,
            // Keep final layout clean; don't clear opacity.
            clearProps: 'transform,willChange'
        });
    }
};

export default animateIn;
